/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { countBy } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Field, Form } from 'react-final-form';
import { BrandColors, Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { getUserPreference, updateUserPreference } from 'blb-api/users';
import { MeContext } from 'blb-contexts/Me';
// TODO: Move Task, TaskModal, etc... to root components directory
import { TaskModal } from 'blb-components/AuthenticatedApp/DashboardPage/TaskModal';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

import { getTenantCheckboxConfig, getClosingTypeCheckboxConfig } from './inputConfigs';

export const styles = StyleSheet.create({
  list: {
    border: `1px solid ${BrandColors.CLOUD_30}`,
    maxHeight: 315,
    overflowY: 'scroll',
    padding: 8,
  },
  selectedDisplay: {
    padding: '8px 0',
  },
});

export namespace AssignmentPreferencesModal {
  export interface Props {
    handleClose: () => void;
    isOpen: boolean;
  }
}

const headerText = 'Assignment preferences';

export const AssignmentPreferencesModal: React.FC<AssignmentPreferencesModal.Props> = ({ isOpen, handleClose }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [areAllTenantsSelected, setAreAllTenantsSelected] = useState<boolean>(true);
  const [areAllClosingTypesSelected, setAreAllClosingTypesSelected] = useState<boolean>(true);
  const [assignmentPreferences, setAssignmentPreferences] = useState<BlendBridge.API.UserPreferenceResponse>({
    canBeAssigned: false,
    excludedTenantCount: 0,
    tenants: [],
    excludedClosingTypeCount: 0,
    closingTypes: [],
  });

  const me = useContext(MeContext);
  const Toaster = useToasts();

  useEffect(() => {
    const fetchPreferences = async () => {
      const preferences = await getUserPreference(me!.principalId);
      setAssignmentPreferences(preferences.data);
      setAreAllTenantsSelected(preferences.data.excludedTenantCount === 0);
      setAreAllClosingTypesSelected(preferences.data.excludedClosingTypeCount === 0);
      setIsLoading(false);
    };

    void fetchPreferences();
  }, []);

  /**
   * Either select or deselect all tenants in the preferences tenant list.
   */
  const toggleAllTenantsChecked = (): void => {
    const wereAllTenantsSelected = areAllTenantsSelected;
    setAssignmentPreferences({
      ...assignmentPreferences,
      excludedTenantCount: wereAllTenantsSelected ? assignmentPreferences.tenants.length : 0,
      tenants: assignmentPreferences.tenants.map(tenant => ({
        ...tenant,
        isExcluded: wereAllTenantsSelected, // toggle checkbox for each tenant
      })),
    });
    setAreAllTenantsSelected(!wereAllTenantsSelected);
  };

  const toggleTenantChecked = (tenantName: string): void => {
    const updatedTenants: BlendBridge.API.TenantPreference[] = assignmentPreferences.tenants.map(
      (tenant: BlendBridge.API.TenantPreference) =>
        tenantName === tenant.name ? { ...tenant, isExcluded: !tenant.isExcluded } : tenant,
    );

    // countBy returns { false: number, true: number } since isExcluded is a boolean
    const excludedTenantCount = countBy(updatedTenants, tenant => tenant.isExcluded).true || 0;
    setAreAllTenantsSelected(excludedTenantCount === 0);

    setAssignmentPreferences({
      ...assignmentPreferences,
      excludedTenantCount,
      tenants: updatedTenants,
    });
  };

  /**
   * Either select or deselect all closingTypes in the preferences closingType list.
   */
  const toggleAllClosingTypesChecked = (): void => {
    const wereAllClosingTypesSelected = areAllClosingTypesSelected;
    setAssignmentPreferences({
      ...assignmentPreferences,
      excludedClosingTypeCount: wereAllClosingTypesSelected ? assignmentPreferences.closingTypes.length : 0,
      closingTypes: assignmentPreferences.closingTypes.map(closingType => ({
        ...closingType,
        isExcluded: wereAllClosingTypesSelected, // toggle checkbox for each closing type
      })),
    });
    setAreAllClosingTypesSelected(!wereAllClosingTypesSelected);
  };

  /**
   * Toggle closingType.isExcluded in the preferences ClosingType list for closing type with name closingTypeName.
   * Update preferences excludedClosingTypeCount based on the new ClosingType list.
   * Update areAllClosingTypesSelected based on whether all closing types are now selected (i.e., not excluded) or not.
   */
  const toggleClosingTypeChecked = (closingTypeName: string): void => {
    const updatedClosingTypes: BlendBridge.API.ClosingTypePreference[] = assignmentPreferences.closingTypes.map(
      (closingType: BlendBridge.API.ClosingTypePreference) =>
        closingTypeName === closingType.name ? { ...closingType, isExcluded: !closingType.isExcluded } : closingType,
    );

    // countBy returns { false: number, true: number } since isExcluded is a boolean
    const excludedClosingTypeCount = countBy(updatedClosingTypes, closingType => closingType.isExcluded).true || 0;
    setAreAllClosingTypesSelected(excludedClosingTypeCount === 0);

    setAssignmentPreferences({
      ...assignmentPreferences,
      excludedClosingTypeCount,
      closingTypes: updatedClosingTypes,
    });
  };

  const getTenantListHeader = () =>
    assignmentPreferences.excludedTenantCount === 0
      ? 'All lenders'
      : `${assignmentPreferences.tenants.length - assignmentPreferences.excludedTenantCount} selected`;

  const getClosingTypeListHeader = () =>
    assignmentPreferences.excludedClosingTypeCount === 0
      ? 'All closing types'
      : `${assignmentPreferences.closingTypes.length - assignmentPreferences.excludedClosingTypeCount} selected`;

  // Disabled if no tenants are selected or no closingTypes are selected or the component is in a loading state
  const isSubmitDisabled = () =>
    assignmentPreferences.excludedTenantCount === assignmentPreferences.tenants.length ||
    assignmentPreferences.excludedClosingTypeCount === assignmentPreferences.closingTypes.length ||
    isLoading;

  const patchPreferences = async () => {
    const body: BlendBridge.API.PatchUserPreferenceBody = {
      excludedTenants: assignmentPreferences.tenants.filter(tenant => tenant.isExcluded).map(tenant => tenant.name),
      excludedClosingTypes: assignmentPreferences.closingTypes
        .filter(closingType => closingType.isExcluded)
        .map(closingType => closingType.name),
      service: 'ECLOSE',
    };
    setIsLoading(true);
    await updateUserPreference(me!.principalId, body);
    setIsLoading(false);
    Toaster!.addToast({
      message: 'Preferences updated successfully',
      theme: 'info',
    });
    handleClose();
  };

  return (
    <Form onSubmit={patchPreferences}>
      {({ handleSubmit }) => (
        <TaskModal
          divideFooter
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          isOpen={isOpen}
          subTitle="Select what types of loans should be assigned to you."
          submitButtonDisabled={isSubmitDisabled()}
          submitButtonLabel="Save"
          title={headerText}
        >
          {isLoading ? (
            <CenteredLoadingSpinner />
          ) : (
            <fieldset>
              <legend>
                <Text>Closing Type</Text>
              </legend>
              <p className={css(styles.selectedDisplay)} id="it-closingTypeListHeader">
                <Text>{getClosingTypeListHeader()}</Text>
              </p>
              <div className={css(styles.list)}>
                <Field
                  {...getClosingTypeCheckboxConfig(
                    {
                      displayName: 'All closing types',
                      isExcluded: !areAllClosingTypesSelected,
                      name: 'allClosingTypes',
                    },
                    toggleAllClosingTypesChecked,
                  )}
                />
                {assignmentPreferences.closingTypes.map((closingType: BlendBridge.API.ClosingTypePreference) => (
                  <Field
                    key={closingType.name}
                    {...getClosingTypeCheckboxConfig(closingType, () => toggleClosingTypeChecked(closingType.name))}
                  />
                ))}
              </div>
              <legend>
                <Text>Lender</Text>
              </legend>
              <p className={css(styles.selectedDisplay)} id="it-tenantListHeader">
                <Text>{getTenantListHeader()}</Text>
              </p>
              <div className={css(styles.list)}>
                <Field
                  {...getTenantCheckboxConfig(
                    { displayName: 'All lenders', isExcluded: !areAllTenantsSelected, name: 'allLenders' },
                    toggleAllTenantsChecked,
                  )}
                />
                {assignmentPreferences.tenants.map((tenant: BlendBridge.API.TenantPreference) => (
                  <Field
                    key={tenant.name}
                    {...getTenantCheckboxConfig(tenant, () => toggleTenantChecked(tenant.name))}
                  />
                ))}
              </div>
            </fieldset>
          )}
        </TaskModal>
      )}
    </Form>
  );
};
